import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from '../store/store';
import { clearUser } from '../store/slices/userSlice';
import API_CONFIG from '../config/api';
import { captureEmailSignInPrompt, isGuestAuthFlowPath } from '../utils/emailSignInPrompt';
import { extractAppNameFromPath, isKioskRoute } from '../utils/restaurantRoutes';
import { kioskLoginPath } from '../components/Kiosk/kioskPaths';

let authRedirectPending = false;

function redirectToSignInOnce(): void {
  if (authRedirectPending) return;
  authRedirectPending = true;
  store.dispatch(clearUser());
  if (typeof window === 'undefined') return;
  const pathname = window.location.pathname;
  if (isKioskRoute(pathname)) {
    const appName = extractAppNameFromPath(pathname);
    if (appName && !pathname.includes('/login')) {
      window.location.href = kioskLoginPath(appName);
    }
    return;
  }
  if (pathname !== '/signin') {
    window.location.href = '/signin';
  }
}

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: true, // Important for cookie-based auth - sends cookies with requests
});

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Token is now stored in httpOnly cookie, so it's automatically sent with requests
    // No need to manually add Authorization header
    // If you need to support both cookie and header, you can add header as fallback
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const isTableRequestEndpoint = requestUrl.includes('/table-request-from-qr');
      const isGuestFlowPage = isGuestAuthFlowPath(pathname);
      const isEmailSignInLanding = captureEmailSignInPrompt();
      const isPublicConfigRequest =
        requestUrl.includes('/api/media/config') ||
        requestUrl.includes('/api/platform/config');
      const isAuthMeRequest = requestUrl.includes('/api/auth/me');
      const isSocialAuthRequest = requestUrl.includes('/api/auth/social');
      const isPasswordResetFlow =
        requestUrl.includes('/api/auth/reset-password') ||
        requestUrl.includes('/api/auth/forgot-password');
      const isLogoutRequest = requestUrl.includes('/api/auth/logout');
      const isStaffJsonApi =
        (requestUrl.includes('/manage/') && requestUrl.includes('/api/')) ||
        requestUrl.includes('/api/notifications');

      // Don't clear user or redirect for table request endpoint or menu page
      // These are handled by the component (might be cookie timing issue)
      if (!isPasswordResetFlow && !isLogoutRequest && !isStaffJsonApi && !isAuthMeRequest
          && !isSocialAuthRequest && !isPublicConfigRequest && !isTableRequestEndpoint
          && !isGuestFlowPage && !isEmailSignInLanding) {
        redirectToSignInOnce();
      }
      // Guest flow pages and email sign-in links show an in-page login modal on 401
    }
    return Promise.reject(error);
  }
);

export default apiClient;

